import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { Cookie, Shield, BarChart3, Target, Settings, ExternalLink, Check, X } from 'lucide-react';

export const CookiePolicy = () => {
  const lastUpdated = "5 Gennaio 2026";
  const [cookiePrefs, setCookiePrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Load current preferences
    const saved = localStorage.getItem('cookiePreferences');
    if (saved) {
      setCookiePrefs(JSON.parse(saved));
    }
  }, []);

  const cookieCategories = [
    {
      id: 'necessary',
      name: 'Cookie Necessari',
      icon: Shield,
      required: true,
      description: 'Essenziali per il funzionamento del sito. Non possono essere disattivati.',
      retention: 'Sessione / 12 mesi',
      cookies: [
        { name: 'session_id', purpose: 'Gestione sessione utente', duration: 'Sessione' },
        { name: 'auth_token', purpose: 'Autenticazione utente', duration: '7 giorni' },
        { name: 'cookieConsent', purpose: 'Memorizzazione consenso cookie', duration: '12 mesi' },
        { name: 'XSRF-TOKEN', purpose: 'Protezione CSRF', duration: 'Sessione' }
      ]
    },
    {
      id: 'analytics',
      name: 'Cookie Analitici',
      icon: BarChart3,
      required: false,
      description: 'Ci aiutano a capire come i visitatori interagiscono con il sito, raccogliendo informazioni in forma anonima.',
      retention: '26 mesi',
      cookies: [
        { name: '_ga', purpose: 'Google Analytics - Distingue utenti', duration: '2 anni' },
        { name: '_ga_*', purpose: 'Google Analytics - Mantiene stato sessione', duration: '2 anni' },
        { name: '_gid', purpose: 'Google Analytics - Distingue utenti', duration: '24 ore' },
        { name: '_gat', purpose: 'Google Analytics - Limita richieste', duration: '1 minuto' }
      ]
    },
    {
      id: 'marketing',
      name: 'Cookie di Marketing',
      icon: Target,
      required: false,
      description: 'Utilizzati per mostrare annunci pertinenti e misurare l\'efficacia delle campagne pubblicitarie.',
      retention: '90 giorni - 2 anni',
      cookies: [
        { name: '_fbp', purpose: 'Facebook Pixel - Tracciamento conversioni', duration: '90 giorni' },
        { name: 'fr', purpose: 'Facebook - Pubblicità mirata', duration: '90 giorni' },
        { name: '_gcl_au', purpose: 'Google Ads - Conversioni', duration: '90 giorni' }
      ]
    },
    {
      id: 'preferences',
      name: 'Cookie di Preferenza',
      icon: Settings,
      required: false,
      description: 'Permettono al sito di ricordare le tue preferenze (lingua, regione, impostazioni).',
      retention: '12 mesi',
      cookies: [
        { name: 'user_preferences', purpose: 'Preferenze interfaccia', duration: '12 mesi' },
        { name: 'theme', purpose: 'Tema chiaro/scuro', duration: '12 mesi' },
        { name: 'language', purpose: 'Lingua preferita', duration: '12 mesi' }
      ]
    }
  ];

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-16 px-4" style={{background: 'linear-gradient(135deg, rgba(127, 179, 160, 0.15) 0%, rgba(242, 239, 233, 0.6) 100%)'}}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Cookie className="w-10 h-10" style={{color: '#2A9FCB'}} />
            <h1 className="text-4xl font-bold" style={{color: '#2A9FCB'}}>Cookie Policy</h1>
          </div>
          <p className="text-[#2A9FCB]/80">Informativa sull'utilizzo dei cookie</p>
          <p className="text-sm text-[#2A9FCB]/70 mt-2">Ultimo aggiornamento: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12" style={{border: '1px solid #E5E7EB'}}>
            
            {/* Intro */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2A9FCB'}}>Cosa sono i Cookie?</h2>
              <p className="text-[#2A9FCB] mb-4">
                I cookie sono piccoli file di testo che i siti web salvano sul tuo dispositivo (computer, tablet, smartphone) 
                quando li visiti. Sono ampiamente utilizzati per far funzionare i siti web in modo più efficiente, 
                migliorare l'esperienza utente e fornire informazioni ai proprietari del sito.
              </p>
              <p className="text-[#2A9FCB]">
                Questa Cookie Policy è conforme all'art. 122 del D.Lgs. 196/2003 (Codice Privacy) come modificato dal 
                D.Lgs. 101/2018, alle Linee Guida del Garante Privacy del 10 giugno 2021, e al Regolamento UE 2016/679 (GDPR).
              </p>
            </div>

            {/* Titolare */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2A9FCB'}}>Titolare del Trattamento</h2>
              <div className="p-4 rounded-xl" style={{background: '#F9FAFB'}}>
                <p className="font-semibold text-[#2A9FCB]">PRIME VISION</p>
                <p className="text-sm text-[#2A9FCB]/80">Via Santa Sofia 10 - 20122 Milano (MI)</p>
                <p className="text-sm text-[#2A9FCB]/80">Email: privacy@primevision.it | PEC: info@primevision.it</p>
              </div>
            </div>

            {/* Tipologie */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2A9FCB'}}>Tipologie di Cookie Utilizzati</h2>
              
              <div className="space-y-6">
                {cookieCategories.map((category) => (
                  <div key={category.id} className="rounded-xl overflow-hidden" style={{border: '1px solid #E5E7EB'}}>
                    {/* Category Header */}
                    <div className="p-4 flex items-center justify-between" style={{background: category.required ? 'rgba(127, 179, 160, 0.1)' : '#F9FAFB'}}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: category.required ? '#2A9FCB' : '#E5E7EB'}}>
                          <category.icon className={`w-5 h-5 ${category.required ? 'text-white' : 'text-[#2A9FCB]/80'}`} />
                        </div>
                        <div>
                          <h3 className="font-bold text-[#2A9FCB]">{category.name}</h3>
                          <p className="text-xs text-[#2A9FCB]/70">Conservazione: {category.retention}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {category.required ? (
                          <span className="px-3 py-1 rounded-full  text-xs font-medium bg-green-100 text-green-700">
                            Sempre attivi
                          </span>
                        ) : (
                          <span className={`px-3 py-1 rounded-full  text-xs font-medium ${cookiePrefs[category.id] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-[#2A9FCB]/80'}`}>
                            {cookiePrefs[category.id] ? 'Attivi' : 'Disattivati'}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Category Description */}
                    <div className="p-4 border-t" style={{borderColor: '#E5E7EB'}}>
                      <p className="text-sm text-[#2A9FCB]/80 mb-4">{category.description}</p>
                      
                      {/* Cookie List */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr style={{background: '#F9FAFB'}}>
                              <th className="p-2 text-left font-semibold text-[#2A9FCB]">Nome Cookie</th>
                              <th className="p-2 text-left font-semibold text-[#2A9FCB]">Finalità</th>
                              <th className="p-2 text-left font-semibold text-[#2A9FCB]">Durata</th>
                            </tr>
                          </thead>
                          <tbody>
                            {category.cookies.map((cookie, idx) => (
                              <tr key={idx} className="border-t" style={{borderColor: '#E5E7EB'}}>
                                <td className="p-2 font-mono text-[#2A9FCB]">{cookie.name}</td>
                                <td className="p-2 text-[#2A9FCB]/80">{cookie.purpose}</td>
                                <td className="p-2 text-[#2A9FCB]/70">{cookie.duration}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cookie di Terze Parti */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2A9FCB'}}>Cookie di Terze Parti</h2>
              <p className="text-[#2A9FCB] mb-4">
                Alcuni cookie sono installati da servizi di terze parti che compaiono sulle nostre pagine. 
                Non abbiamo controllo su questi cookie. Di seguito i link alle rispettive privacy policy:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { name: 'Google Analytics', url: 'https://policies.google.com/privacy' },
                  { name: 'Google Ads', url: 'https://policies.google.com/technologies/ads' },
                  { name: 'Facebook', url: 'https://www.facebook.com/privacy/policy/' },
                  { name: 'YouTube', url: 'https://policies.google.com/privacy' }
                ].map((service, idx) => (
                  <a 
                    key={idx}
                    href={service.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    style={{border: '1px solid #E5E7EB'}}
                  >
                    <ExternalLink className="w-4 h-4" style={{color: '#2A9FCB'}} />
                    <span className="text-sm text-[#2A9FCB]">{service.name}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Come Gestire i Cookie */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2A9FCB'}}>Come Gestire i Cookie</h2>
              
              <div className="space-y-4">
                <div className="p-4 rounded-xl" style={{background: 'rgba(127, 179, 160, 0.1)'}}>
                  <h3 className="font-semibold mb-2" style={{color: '#2A9FCB'}}>1. Tramite il nostro Banner</h3>
                  <p className="text-sm text-[#2A9FCB]">
                    Al primo accesso al sito, puoi scegliere quali categorie di cookie accettare tramite il banner. 
                    Puoi modificare le tue preferenze in qualsiasi momento cliccando su "Gestisci Cookie" nel footer.
                  </p>
                </div>
                
                <div className="p-4 rounded-xl" style={{background: '#F9FAFB'}}>
                  <h3 className="font-semibold text-[#2A9FCB] mb-2">2. Tramite il Browser</h3>
                  <p className="text-sm text-[#2A9FCB] mb-3">
                    Puoi gestire i cookie anche attraverso le impostazioni del tuo browser:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { name: 'Chrome', url: 'https://support.google.com/chrome/answer/95647' },
                      { name: 'Firefox', url: 'https://support.mozilla.org/it/kb/Gestione%20dei%20cookie' },
                      { name: 'Safari', url: 'https://support.apple.com/it-it/guide/safari/sfri11471/mac' },
                      { name: 'Edge', url: 'https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge' }
                    ].map((browser, idx) => (
                      <a
                        key={idx}
                        href={browser.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-center p-2 rounded bg-white hover:bg-gray-50"
                        style={{border: '1px solid #E5E7EB', color: '#2A9FCB'}}
                      >
                        {browser.name}
                      </a>
                    ))}
                  </div>
                </div>
                
                <div className="p-4 rounded-xl" style={{background: '#F9FAFB'}}>
                  <h3 className="font-semibold text-[#2A9FCB] mb-2">3. Opt-out Specifici</h3>
                  <p className="text-sm text-[#2A9FCB] mb-3">
                    Per disattivare specifici servizi di tracciamento:
                  </p>
                  <div className="space-y-2">
                    <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" 
                       className="flex items-center gap-2 text-sm" style={{color: '#2A9FCB'}}>
                      <ExternalLink className="w-3 h-3" />
                      Google Analytics Opt-out Browser Add-on
                    </a>
                    <a href="https://www.youronlinechoices.com/it/le-tue-scelte" target="_blank" rel="noopener noreferrer"
                       className="flex items-center gap-2 text-sm" style={{color: '#2A9FCB'}}>
                      <ExternalLink className="w-3 h-3" />
                      Your Online Choices (EDAA)
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Conseguenze Disattivazione */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2A9FCB'}}>Conseguenze della Disattivazione</h2>
              <div className="p-4 rounded-xl" style={{background: '#FEF3C7', border: '1px solid #FDE68A'}}>
                <p className="text-sm text-amber-800">
                  <strong>Attenzione:</strong> Disattivando i cookie necessari, alcune funzionalità del sito potrebbero non funzionare correttamente. 
                  In particolare, potresti non essere in grado di effettuare il login, prenotare appuntamenti o utilizzare l'area riservata.
                </p>
              </div>
            </div>

            {/* Diritti */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2A9FCB'}}>I Tuoi Diritti</h2>
              <p className="text-[#2A9FCB] mb-4">
                Ai sensi del GDPR, hai il diritto di accedere, rettificare, cancellare i tuoi dati, 
                limitarne il trattamento, opporti al trattamento e richiederne la portabilità.
              </p>
              <p className="text-[#2A9FCB]">
                Per maggiori informazioni sui tuoi diritti, consulta la nostra{' '}
                <Link to="/privacy" className="underline" style={{color: '#2A9FCB'}}>Privacy Policy</Link>.
              </p>
            </div>

            {/* Aggiornamenti */}
            <div>
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2A9FCB'}}>Aggiornamenti</h2>
              <p className="text-[#2A9FCB]">
                Questa Cookie Policy può essere aggiornata periodicamente. Ti invitiamo a consultare regolarmente questa pagina. 
                La data dell'ultimo aggiornamento è indicata in cima alla pagina.
              </p>
            </div>

          </div>

          {/* Gestisci Cookie Button */}
          <div className="mt-8 text-center">
            <button
              onClick={() => {
                localStorage.removeItem('cookieConsent');
                localStorage.removeItem('cookiePreferences');
                window.location.reload();
              }}
              className="px-6 py-3 rounded-full  font-medium inline-flex items-center gap-2"
              style={{background: '#2A9FCB', color: 'white'}}
            >
              <Settings className="w-5 h-5" />
              Gestisci le tue preferenze Cookie
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiePolicy;
